"use client";

import Image from "next/image";
import Container from "../CustomContainer";
import { useState } from "react";
import Link from "next/link";
import { FooterData } from "./footer";
import { DIWALI_FOOTER, DiwaliFooterDecor } from "../../DiwaliTheme";

const data = {
  importantLinks: [
    { label: "About Us", href: "/about-us" },
    { label: "Contact Us", href: "/contact-us" },
    { label: "Privacy Policy", href: "/privacy-policy" },
    { label: "Terms and Condition", href: "/terms-and-condition" },
    { label: "Latest News", href: "/news" },
  ],
  quickLinks: [
    { label: "New Cars", href: "/new-cars" },
    { label: "Latest Cars", href: "/latest-cars" },
    { label: "Upcoming Cars", href: "/upcoming-cars" },
    { label: "Popular Cars", href: "/popular-cars" },
    { label: "Electric Cars", href: "/electric-cars" },
    { label: "Compare Cars", href: "/compare-cars" }
  ],
  brands: [
    { name: "Mahindra", href: "/mahindra" },
    { name: "TATA", href: "/tata" },
    { name: "Kia", href: "/kia" },
    { name: "Hyundai", href: "/hyundai" },
    { name: "Maruti Suzuki", href: "/maruti" },
    { name: "Toyota", href: "/toyota" }
  ],
  groupVentures: [
    { name: "Truck Junction", href: "https://trucks.tractorjunction.com/", logo: "cj-brand-logo/truck-logo.svg" },
    { name: "Tractor Junction", href: "https://www.tractorjunction.com/", logo: "cj-brand-logo/tractor-logo.svg" },
    { name: "Infra Junction", href: "https://infra.tractorjunction.com/", logo: "cj-brand-logo/infra-logo.svg" },
    { name: "TractorGuru", href: "https://tractorguru.in/", logo: "cj-brand-logo/tractorguru-logo.svg" },
    { name: "Finj", href: "https://finj.in/", logo: "cj-brand-logo/finj-logo.svg" },
    { name: "Bike Junction", href: "https://thebikejunction.com/", logo: "cj-brand-logo/bike-logo.svg" }
  ],
  socialLinks: [
    { label: "Facebook", href: "https://www.facebook.com/mycarjunction/", SocialIcon: "social-icon/facebook.svg" },
    // { label: "Twitter", href: "#", SocialIcon: "social-icon/X.svg" },
    { label: "YouTube", href: "https://www.youtube.com/@my-carjunction/videos", SocialIcon: "social-icon/youtube.svg" },
    { label: "Instagram", href: "https://www.instagram.com/mycarjunction/", SocialIcon: "social-icon/instagram.svg" },
    { label: "LinkedIn", href: "https://www.linkedin.com/company/car-junction-india/", SocialIcon: "social-icon/linkedin.svg" }
  ]
} as FooterData;

const HEADING_CLASS = `text-white font-semibold text-base mb-3.5 ${DIWALI_FOOTER.heading}`;
const LINK_CLASS = `text-white no-underline text-sm font-normal transition-colors duration-200 hover:text-[#94a3b8] ${DIWALI_FOOTER.link}`;

const Footer = () => {
  const [openSection, setOpenSection] = useState("important");

  const toggleSection = (section: string) => {
    setOpenSection((prev) => (prev === section ? "" : section));
  };

  const accordionSection = (id: string, label: string, items: { label?: string; name?: string; href: string }[]) => (
    <div className={`max-[767px]:border-b max-[767px]:border-[#1e2d45] max-[767px]:pb-2.5 ${DIWALI_FOOTER.divider}`}>
      <p
        className={`${HEADING_CLASS} max-[767px]:flex max-[767px]:justify-between max-[767px]:items-center max-[767px]:cursor-pointer md:cursor-default`}
        onClick={() => toggleSection(id)}
      >
        {label}
        <span
          className={`max-[767px]:inline-block max-[767px]:w-2 max-[767px]:h-2 max-[767px]:border-r-2 max-[767px]:border-b-2 max-[767px]:border-white max-[767px]:transition-transform max-[767px]:duration-300 md:hidden ${DIWALI_FOOTER.chevron} ${
            openSection === id ? "max-[767px]:rotate-[-135deg]" : "max-[767px]:rotate-45"
          }`}
        />
      </p>

      <ul
        className={`list-none p-0 m-0 max-[767px]:overflow-hidden max-[767px]:transition-[max-height] max-[767px]:duration-300 max-[767px]:ease-in-out max-[767px]:ml-4 md:max-h-none ${
          openSection === id ? "max-[767px]:max-h-[300px]" : "max-[767px]:max-h-0"
        }`}
      >
        {items.map((item) => (
          <li key={item.label || item.name} className="mb-3">
            {item.href === "#" ? (
              <span className={`${LINK_CLASS} cursor-default`}>
                {item.label || item.name}
              </span>
            ) : (
              <Link prefetch={false} href={item.href} className={LINK_CLASS} title={item.label || item.name}>
                {item.label || item.name}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <footer className={`bg-[#0d1526] text-[#cbd5e1] pt-12 max-[767px]:pt-7 ${DIWALI_FOOTER.root}`}>
      <DiwaliFooterDecor />
      <Container>
        <div className={`grid grid-cols-1 gap-6 border-t border-[#1e2d45] pt-4 md:grid-cols-4 md:gap-4 md:border-t-0 md:pt-0 min-[900px]:gap-10 ${DIWALI_FOOTER.divider}`}>
          {accordionSection("important", "Important Link", data.importantLinks)}
          {accordionSection("quick", "Quick Links", data.quickLinks)}
          {accordionSection("brands", "Brands", data.brands)}

          {/* Group Ventures (NO accordion) */}
          <div>
            <p className={`${HEADING_CLASS} max-[767px]:flex max-[767px]:justify-center`}>Our Group Ventures</p>
            <div className="grid grid-cols-3 gap-6">
              {data.groupVentures.map((item) => (
                <Link prefetch={false} key={item.name} href={item.href} target="_blank" title={item.name}>
                  <Image
                    src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}${item.logo}`}
                    alt={item.name}
                    width={100}
                    height={40}
                    className="w-full h-auto"
                    title={item.name}
                  />
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className={`border-t border-[#0f194f] mt-10 py-5 flex justify-between flex-wrap max-[767px]:justify-center max-[767px]:gap-5 ${DIWALI_FOOTER.divider}`}>
          <p>
            © {new Date().getFullYear()} <Link prefetch={false} href="/" className={`underline ${DIWALI_FOOTER.brandLink}`}>Car Junction</Link>. All
            rights reserved.
          </p>

          <div className="flex gap-2.5">
            {data.socialLinks.map((social) =>
              social.href === "#" ? (
                <span
                  key={social.label}
                  className={`bg-[#94a4ff1a] no-underline w-9 h-9 border border-[#94a4ff1a] flex items-center justify-center rounded-full text-inherit transition-all duration-200 hover:text-white hover:bg-[#94a4ff33] cursor-default ${DIWALI_FOOTER.social}`}
                  aria-label={social.label}
                >
                  <Image
                    src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}${social.SocialIcon}`}
                    alt={social.label}
                    width={20}
                    height={20}
                    className="w-3.5 h-3.5"
                    title={social.label}
                  />
                </span>
              ) : (
                <Link
                  prefetch={false}
                  key={social.label}
                  href={social.href}
                  className={`bg-[#94a4ff1a] no-underline w-9 h-9 border border-[#94a4ff1a] flex items-center justify-center rounded-full text-inherit transition-all duration-200 hover:text-white hover:bg-[#94a4ff33] ${DIWALI_FOOTER.social}`}
                  aria-label={social.label}
                  target="_blank"
                  title={social.label}
                >
                  <Image
                    src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}${social.SocialIcon}`}
                    alt={social.label}
                    width={20}
                    height={20}
                    className="w-3.5 h-3.5"
                    title={social.label}
                  />
                </Link>
              )
            )}
          </div>
        </div>
      </Container>
    </footer>
  );
};

export default Footer;
